module Client.Admin.ManageApplications.Edit

open Shared
open Client
open System
open Elmish
open Illy.SafeStack
open Illy.SafeStack.Client
open Fable.React
open Fable.React.Props
open Shared.Admin.ManageApplications

module Attribute = 
    type Identifier = Identifier of string 
    module Identifier = 
        let unwrap (Identifier x) = x
    type AttributeType = 
        | Checkbox of Check.Model

    module AttributeType = 
        let toDTO = 
            function
            | AttributeType.Checkbox x -> Check.toBool x |> AttributeValueDTO.Checkbox

    type Model = 
        {
            Id: Identifier
            Label: string
            Type: AttributeType
        }

    type Msg = 
        | CheckboxMsg of Check.Msg

    let private initCheck (Identifier id) label value = 
        {Check.CustomSettings.defaultOptions() with RenderInline = true}
        |> Check.init (sprintf "check-%s" id) label false value

    let ofDTO (dto: AttributeDTO) = 
        match dto.Value with  
        | AttributeValueDTO.Checkbox v -> 
            {
                Id = Identifier dto.Name 
                Label = dto.Label
                Type = initCheck (Identifier dto.Name) dto.Label v |> Checkbox
            }

    let toDTO (m: Model) = 
        {
            Name = Identifier.unwrap m.Id
            Label = m.Label
            Value = AttributeType.toDTO m.Type
        }

    let getIdentifier x = x.Id 

    let private updateType msg model = 
        match msg, model with 
        | CheckboxMsg cMsg, Checkbox cm -> 
            let m, c = Check.update cMsg cm 
            Checkbox m, Cmd.map CheckboxMsg c
        
    let update msg model = 
        let m, c = updateType msg model.Type 
        { model with 
            Type= m
        }, c

    let view loading model dispatch = 
        match model.Type with 
        | Checkbox m -> Check.check loading m (CheckboxMsg >> dispatch)

module ApplicationAccess = 
    type Model = 
        | HasAccess of Check.Model
        | UserLevel of NoFlags Select.Model
    
    type Msg = 
        | HasAccessMsg of Check.Msg
        | UserLevelMsg of NoFlags Select.Msg

    module Initialisations = 
        let initHasAccess (associationId: Guid) value = 
            {Check.CustomSettings.defaultOptions() with RenderInline = true}
            |> Check.init (associationId.ToString()) "" false value

        let initUserLevel (associationId: Guid) options value = 
            Select.initWithOptions 
                (sprintf "%s" (associationId.ToString())) 
                "User Level" 
                (Some "No Access")
                true 
                (options |> List.map UserLevel.toListOption |> ListType.Standard)
                (value |> Option.map (UserLevel.toListOption >> ListOptionType.Standard))

    let ofDTO associationId = 
        function 
        | InitApplicationAccessDTO.HasAccess v -> Initialisations.initHasAccess associationId v |> HasAccess 
        | InitApplicationAccessDTO.UserLevel (opts, v) -> Initialisations.initUserLevel associationId opts v |> UserLevel

    let toDTO = 
        function
        | HasAccess x -> x |> Check.toBool |> SaveApplicationAccessDTO.HasAccess 
        | UserLevel x -> x |> Select.toListOption |> Option.map (ListOption.getId >> UserLevel.ofInt) |> SaveApplicationAccessDTO.UserLevel 

    let update msg model = 
        match msg, model with 
        | HasAccess ham, HasAccessMsg haMsg -> 
            let m, c = Check.update haMsg ham 
            HasAccess m, Cmd.map HasAccessMsg c  
        | UserLevel ulm, UserLevelMsg ulMsg -> 
            let m, c = Select.update ulMsg ulm 
            UserLevel m, Cmd.map UserLevelMsg c  
        | _, _ -> failwith "Invalid case"

    let view loading model dispatch = 
        match model with 
        | HasAccess m -> Check.checkInput loading m (HasAccessMsg >> dispatch)
        | UserLevel m -> Select.selectInput loading m (UserLevelMsg >> dispatch)

module Application = 
    type Identifier = Identifier of Guid

    module Identifier = 
        let unwrap (Identifier x) = x
        let toString (Identifier x) = x.ToString()
    type Model = 
        {
            AssociationId: Identifier
            AppId: int
            Image: string
            Name: string
            ApplicationAccess: ApplicationAccess.Model
            Attributes: Attribute.Model list
        }

    let getIdentifier m = m.AssociationId

    type Msg = 
        | AttributeMsg of Attribute.Identifier * Attribute.Msg
        | ApplicationAccessMsg of ApplicationAccess.Msg


    let ofDTO (dto: InitApplicationDTO) = 
        {
            AssociationId = Identifier dto.AssociationId
            AppId = dto.AppId
            Image = dto.Image
            Name = dto.Name
            ApplicationAccess = ApplicationAccess.ofDTO dto.AssociationId dto.ApplicationAccess
            Attributes = dto.Attributes |> List.map Attribute.ofDTO
        }

    let toDTO (m: Model) = 
        {
            AssociationId = Identifier.unwrap m.AssociationId
            AppId = m.AppId
            Name = m.Name
            ApplicationAccess = ApplicationAccess.toDTO m.ApplicationAccess
            Attributes = m.Attributes |> List.map Attribute.toDTO
        }
    let dispatchAttributeMsg model attributeId msg = 
        let m, c = 
            ListItem.dispatchMsgInList 
                attributeId 
                Attribute.getIdentifier 
                model.Attributes
                Attribute.update
                Cmd.none
                msg
        {model with Attributes = m}, c |> Cmd.map (fun x -> AttributeMsg(attributeId, x))

    let update msg model = 
        match msg with 
        | AttributeMsg (aId, aMsg) -> dispatchAttributeMsg model aId aMsg
        | ApplicationAccessMsg uMsg -> 
            let m, c = ApplicationAccess.update model.ApplicationAccess uMsg
            {model with ApplicationAccess = m}, Cmd.map ApplicationAccessMsg c

    let view loading model dispatch = 
        div [
            ClassName "app-container"
        ] [
            if loading then jQueryUI.skeletonScreenIcon
            else img [ Src model.Image ]
            div [
                ClassName "app-content"
            ] [
                div [
                    ClassName "app-content-header"
                ] [
                    jQueryUI.label (Identifier.toString model.AssociationId) model.Name None None loading
                    ApplicationAccess.view loading model.ApplicationAccess (ApplicationAccessMsg >> dispatch)
                ]
                
                if (List.isEmpty >> not) model.Attributes then
                    ul [] [
                        yield! 
                            model.Attributes
                            |> List.map 
                                ((fun a -> Attribute.view loading a ((fun msg -> AttributeMsg(Attribute.getIdentifier a, msg)) >> dispatch))
                                >> List.singleton 
                                >> li [])
                    ]
                   

            ]
        ]

type Model = 
    {
        UserId: UserId 
        UserSummary: string 
        Applications: Application.Model list 
        Loading: bool
        GeneralError: string option
        Saving: bool
    }

type Event = 
    | CloseOverlay 
    | ApiError of ApiError 
    | SaveSuccessful of notification: string 

module Validations = 
    open Illy.SafeStack.Validation
    open Illy.SafeStack.CompositionalValidation
    open Shared.Admin.ManageApplications.Validations

    let resetValidations (m: Model) = 
        {m with 
            GeneralError = None
        }

    let dispatchValidations model = 
        let model = resetValidations model 
        function 
        | ValidationResult.Passed -> model 
        | ValidationResult.Warned _ -> failwith "Did not expect warnings"
        | ValidationResult.Failed (err, _) ->
            let errors = err |> List.map ManageApplicationsValidations.toString |> concatOptional
            {model with GeneralError = errors}

type Msg = 
    | ApplicationMsg of Application.Identifier * Application.Msg
    | ApiError of ApiError
    | InitDataLoaded of InitUserDTO
    | CancelClicked
    | SaveClicked
    | SaveResult of FailureReason option

let init userId = 
    {
        UserId = userId
        UserSummary = ""
        Applications = []
        Loading = true
        Saving = false
        GeneralError = None
    }, Api.secureCall<IManageApplicationsApi,_,_,_> SecuredRoute.Admin (fun api -> api.getInitApplicationsData) userId InitDataLoaded ApiError

let toDTO (m: Model) = 
    {
        UserId = UserId.unwrap m.UserId
        Applications = m.Applications |> List.map Application.toDTO
    }

let dispatchApplicationMsg model appIdentifier msg = 
    let m, c = 
        ListItem.dispatchMsgInList 
            appIdentifier 
            Application.getIdentifier 
            model.Applications
            Application.update
            Cmd.none
            msg
    {model with Applications = m}, c |> Cmd.map (fun x -> ApplicationMsg(appIdentifier, x)) |> SideEffect.Cmd

let update model msg = 
    match msg with 
    | InitDataLoaded d -> 
        {model with 
            UserSummary = sprintf "%s %s (%s)" d.Forename d.Surname d.Username
            Applications = d.Applications |> List.map Application.ofDTO
            Loading = false
        }, SideEffect.none
    | ApiError err -> model, err |> Event.ApiError |> SideEffect.Event 
    | SaveClicked ->
        let dto = toDTO model
        {model with Saving = true},
        Api.secureCall<IManageApplicationsApi,_,_,_> SecuredRoute.Admin (fun api -> api.updateApplications) (toDTO model) SaveResult ApiError
        |> SideEffect.Cmd
    | SaveResult reason -> 
        match reason with 
        | None
        | Some FailureReason.NothingToDo -> model, "User Applications were modified successfully." |> Event.SaveSuccessful |> SideEffect.Event
        | Some (FailureReason.FailedValidations v) ->
            Validations.dispatchValidations {model with Saving = false} v, SideEffect.none
    | Msg.CancelClicked -> model, CloseOverlay |> SideEffect.Event 
    | ApplicationMsg (aId, aMsg) -> dispatchApplicationMsg model aId aMsg

let skeletonApp() = 
    {
        AssociationId = Guid.NewGuid()
        AppId = 1
        Image = ""
        Name = ""
        ApplicationAccess = InitApplicationAccessDTO.HasAccess false
        Attributes = []
    }

let skeletonScreen() = 
    [
        skeletonApp()
        skeletonApp()
        skeletonApp()
    ]
    |> List.map Application.ofDTO

let view model dispatch = 
    let title = if model.Loading then "Loading..." else model.UserSummary
    jQueryUI.modal title 50 Msg.CancelClicked dispatch (Some 510) [
        div [
            ClassName "apps"
        ] [
            h2 [] [ str "Application Access"] 
            yield! 
                if model.Loading then skeletonScreen() else model.Applications
                |> List.map (fun a -> Application.view model.Loading a ((fun msg -> ApplicationMsg(Application.getIdentifier a, msg)) >> dispatch))
        ]
        
        UI.renderError model.GeneralError
        yield! UI.saveControls None model.Loading model.Saving SaveClicked CancelClicked dispatch
    ]